import { activeRecommendationsQueryOptions } from '@/features/claire/api/use-active-recommendations';
import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { conversationMessageSchema } from '@borradh-workspace/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ConversationMessage, SendMessageInput } from '../types';

export const useSendMessage = (options?: {
  onSuccess?: (message: ConversationMessage) => void;
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    // A reply is an external side effect; an ambiguous failure needs review.
    retry: false,
    mutationFn: ({
      conversationId,
      ...input
    }: SendMessageInput & { conversationId: string }) =>
      apiClient.post<ConversationMessage>(
        `conversations/${conversationId}/messages`,
        input,
        { schema: conversationMessageSchema }
      ),
    onSettled: (_message, _error, variables) => {
      // Refresh the policy state too: a stale composer can receive a suppression
      // refusal. This prefix also refreshes the conversation message list.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(variables.conversationId),
      });
      void queryClient.invalidateQueries({
        queryKey: ['conversations', 'list'],
      });
      // The merged inbox shows the reply in whichever conversation is open,
      // which need not be the thread it went out on: refresh every timeline.
      void queryClient.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === 'conversations' && q.queryKey[2] === 'timeline',
      });
      // A reply moves the thread out of Needs Reply; without this the tab
      // count and the Inbox badge stay stale until their next poll.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.tabCountsAll(),
      });
    },
    onSuccess: (message) => {
      // A reply can clear the home card's "a lead is waiting" nudge; the API
      // hides it on read, so refetch rather than serve the cached copy.
      void queryClient.invalidateQueries({
        queryKey: activeRecommendationsQueryOptions().queryKey,
      });
      options?.onSuccess?.(message);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to send message');
    },
  });

  return {
    sendMessage: mutation.mutate,
    sendMessageAsync: mutation.mutateAsync,
    isSending: mutation.isPending,
  };
};
